/**
 * Popover menu from the logo click: Home, legal pages, About/Contact dialogs.
 * `onCloseParent` closes the host drawer when navigating (side menu or AppDrawer).
 * With `appRouter`, About/Contact/Privacy/Terms use in-app dialog routing (`/app`).
 */
import { appConfig } from "@/config/app-config";
import { AboutDialog } from "@/lib/nemmp/base/about/AboutDialog";
import { ContactDialog } from "@/lib/nemmp/base/contact/ContactDialog";
import { contactDialogConfig } from "@/lib/nemmp/starter/app/contactDialogConfig";
import { recordEvent } from "@/lib/nemmp/base/tracking/usage/recordEvent";
import { Router } from "@/lib/nemmp/starter/routing/Router";
import type { DialogName } from "@/lib/nemmp/starter/routing/types";
import BadgeIcon from "@mui/icons-material/Badge";
import GavelIcon from "@mui/icons-material/Gavel";
import HomeIcon from "@mui/icons-material/Home";
import MailIcon from "@mui/icons-material/Mail";
import PolicyIcon from "@mui/icons-material/Policy";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Link from "next/link";
import type { ReactNode } from "react";
import { observer } from "mobx-react";
import { logoInfoMenuState } from "./LogoInfoMenuState";

type LogoInfoMenuProps = {
  onCloseParent?: () => void;
  appRouter?: Router;
};

export const LogoInfoMenu = observer(
  ({ onCloseParent, appRouter }: LogoInfoMenuProps) => {
    const openAppDialog = (eventName: string, dialog: DialogName) => {
      recordEvent(eventName);
      logoInfoMenuState.closeLogoMenu();
      appRouter?.openDialog(dialog);
    };

    return (
      <>
        <Menu
          anchorEl={logoInfoMenuState.logoMenuAnchor}
          open={logoInfoMenuState.isOpen}
          onClose={() => logoInfoMenuState.closeLogoMenu()}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          transformOrigin={{ horizontal: "left", vertical: "top" }}
        >
          <LogoInfoMenuItem
            label="Home"
            icon={<HomeIcon fontSize="small" />}
            href="/"
            onCloseParent={onCloseParent}
          />
          <Divider />
          <LogoInfoMenuItem
            label="About"
            icon={<BadgeIcon fontSize="small" />}
            onClick={() => {
              if (appRouter) {
                openAppDialog("click-app-drawer-about", "about");
                return;
              }
              recordEvent("click-logo-info-menu-about");
              logoInfoMenuState.closeLogoMenu();
              logoInfoMenuState.aboutDialog.open();
            }}
          />
          <LogoInfoMenuItem
            label="Terms of Service"
            icon={<GavelIcon fontSize="small" />}
            href={appRouter ? undefined : "/terms"}
            onClick={
              appRouter
                ? () => openAppDialog("click-app-drawer-terms", "terms")
                : undefined
            }
            onCloseParent={onCloseParent}
          />
          <LogoInfoMenuItem
            label="Privacy Policy"
            icon={<PolicyIcon fontSize="small" />}
            href={appRouter ? undefined : "/privacy"}
            onClick={
              appRouter
                ? () => openAppDialog("click-app-drawer-privacy", "privacy")
                : undefined
            }
            onCloseParent={onCloseParent}
          />
          <LogoInfoMenuItem
            label="Contact"
            icon={<MailIcon fontSize="small" />}
            onClick={() => {
              if (appRouter) {
                openAppDialog("click-app-drawer-contact", "contact");
                return;
              }
              recordEvent("click-logo-info-menu-contact");
              logoInfoMenuState.closeLogoMenu();
              logoInfoMenuState.contactDialog.open();
            }}
          />
        </Menu>
        {!appRouter && (
          <>
            <AboutDialog
              cx={{ appConfig }}
              open={logoInfoMenuState.aboutDialog.isOpen}
              onClose={() => logoInfoMenuState.aboutDialog.close()}
              contactDialogConfig={contactDialogConfig}
            />
            <ContactDialog
              open={logoInfoMenuState.contactDialog.isOpen}
              onClose={() => logoInfoMenuState.contactDialog.close()}
              config={contactDialogConfig}
            />
          </>
        )}
      </>
    );
  },
);

function LogoInfoMenuItem({
  label,
  icon,
  href,
  onClick,
  onCloseParent,
}: {
  label: string;
  icon: ReactNode;
  href?: string;
  onClick?: () => void;
  onCloseParent?: () => void;
}) {
  const closeOnNavigate = () => {
    logoInfoMenuState.closeLogoMenu();
    onCloseParent?.();
  };

  if (href) {
    return (
      <MenuItem component={Link} href={href} onClick={closeOnNavigate}>
        <ListItemIcon>{icon}</ListItemIcon>
        {label}
      </MenuItem>
    );
  }

  return (
    <MenuItem onClick={onClick}>
      <ListItemIcon>{icon}</ListItemIcon>
      {label}
    </MenuItem>
  );
}
